import {
  useDecodedAccessToken,
  useAccessTokenRoles,
} from '@b-stock/fe-core/bstock-next/components'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedAuctionListingDetails,
  getSelectedQuoteQuery,
  myLocationsQuery,
  isOfferingListing,
} from '@b-stock/fe-core/bstock-next/queries'
import { LocationAttribute } from '@b-stock/location-api-client'

import {
  ScrollIntoViewId,
  useScrollIntoView,
} from '@/buyer-portal/components/features/listing/ScrollIntoView/ScrollIntoViewContext'

/**
 * Shared brain for the Make an Offer module. Runs the buyer/location/quote
 * queries and applies the visibility gate (ITEM-scope offering listings only),
 * returning the props the section (V1) and card (V2) chrome both render, or
 * `null` when the module should not show.
 */
export const useMakeAnOfferModel = (
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
) => {
  const { refs } = useScrollIntoView()
  const isActiveBuyer = useAccessTokenRoles()?.buyer === 'ACTIVE'
  const accountId = useDecodedAccessToken()?.accountId

  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        accountId: accountId ? [accountId] : [],
      },
    ],
    { enabled: isActiveBuyer }
  )
  const defaultLocation = myLocations.data?.locations[0]

  const { data: selectedQuote } = getSelectedQuoteQuery.useWithOpts(
    [listing._id, accountId],
    { enabled: isActiveBuyer && !!defaultLocation?.id }
  )

  if (!isOfferingListing(listing) || listing.offering?.offerScope !== 'ITEM') {
    return null
  }

  const {
    contract = null,
    isIndexed = false,
    items,
    offers,
    offering,
    status,
  } = listing

  return {
    ref: refs[ScrollIntoViewId.MAKE_AN_OFFER],
    contract,
    currency: listing.currency ?? 'USD',
    isIndexed,
    items,
    offers: offers ?? [],
    offering,
    selectedQuote,
    status,
  }
}

export type MakeAnOfferModel = NonNullable<
  ReturnType<typeof useMakeAnOfferModel>
>
